import React, { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import useSWR from 'swr'
import {
  fetchWidgets, createWidget, updateWidget, deleteWidget, fetchWidgetPlacements, Widget, WidgetPlacementSummary
} from '../../services/api'
import {
  getWidgetSizeLabel, getWidgetTypeLabel, isCarouselWidgetType, widgetTypeOptionsForSite,
} from '../../constants/widgetTypes'
import { getPageTypeLabel } from '../../constants/pageTypes'
import { useWidgetSite } from '../../contexts/WidgetSiteContext'
import { useToast } from '../../contexts/ToastContext'
import { Button, Badge, Banner, Panel, Drawer, useSpeedbump } from '../../components'
import { DISCARD_CHANGES_PROMPT } from '../../constants/unsavedChanges'
import { useWidgetFormState } from '../../hooks'
import WidgetForm from '../../components/merchandising/WidgetForm'
import { widgetMetadataSaveError } from '../../components/merchandising/metadata-editors'
import WidgetTypeIcon from '../../components/merchandising/WidgetTypeIcon'
import WidgetTypeBadge from '../../components/merchandising/WidgetTypeBadge'
import { widgetTypeColorClass } from '../../components/merchandising/widgetTypeColors'
import pageStyles from '../../styles/page.module.scss'
import listTableStyles from '../../styles/list-table.module.scss'
import formStyles from '../../styles/forms.module.scss'
import styles from './WidgetLibrary.module.scss'

// Mirrors PageLayout's routes: the placement drawer opens on its page or page-type view.
const placementPath = (p: WidgetPlacementSummary) =>
  p.page ? `/content/page/${p.page.id}/placement/${p.id}`
    : p.page_type ? `/content/${p.page_type}/placement/${p.id}`
      : `/content/placement/${p.id}`

const WidgetLibrary: React.FC = () => {
  const { siteId, siteName, siteCode, setSite } = useWidgetSite()
  // Type-filter options: the four carousel variants collapse to one "Carousel"
  // filter (its code is the default variant), matching the badge and picker.
  const siteWidgetTypes = widgetTypeOptionsForSite(siteCode)
  const { toast } = useToast()
  const confirm = useSpeedbump()
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')
  const [editingWidget, setEditingWidget] = useState<Widget | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [saving, setSaving] = useState(false)
  // ?widget=<id> names the widget whose drawer is open, so a card is a real
  // link: middle-click opens that widget in a new tab.
  const [searchParams, setSearchParams] = useSearchParams()
  const linkedWidgetId = searchParams.get('widget')
  const setLinkedWidget = (id: string | null) =>
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (id) next.set('widget', id)
      else next.delete('widget')
      return next
    }, { replace: true })

  const { data: widgetsData, mutate, error } = useSWR(
    siteId ? ['merch-widgets', siteId] : null,
    () => fetchWidgets(siteId),
    { revalidateOnFocus: false }
  )
  const widgets: Widget[] = widgetsData?.widgets || []

  const formState = useWidgetFormState({
    siteId,
    initialWidget: editingWidget,
    onWidgetsChanged: () => { void mutate() },
  })

  const filtered = widgets.filter(w => {
    if (typeFilter) {
      // A carousel filter matches any of the four carousel variants.
      if (isCarouselWidgetType(typeFilter)) {
        if (!isCarouselWidgetType(w.widget_type)) return false
      } else if (w.widget_type !== typeFilter) {
        return false
      }
    }
    if (searchTerm && !w.name.toLowerCase().includes(searchTerm.toLowerCase())) return false
    return true
  })

  // Fetch placements for the widget being edited
  const { data: widgetPlacementsData, isLoading: placementsLoading } = useSWR(
    formState.widgetId ? ['merch-widget-placements', formState.widgetId] : null,
    () => fetchWidgetPlacements(formState.widgetId!),
    { revalidateOnFocus: false }
  )
  const widgetPlacements: WidgetPlacementSummary[] = widgetPlacementsData?.placements || []

  const openCreate = () => {
    setLinkedWidget(null)
    setEditingWidget(null)
    formState.reset(null)
    setShowCreateForm(true)
  }

  const openEdit = (w: Widget) => {
    setEditingWidget(w)
    formState.reset(w)
    setShowCreateForm(true)
    setLinkedWidget(w.id)
  }

  // Deep link: open the named widget once the list has loaded.
  useEffect(() => {
    if (!linkedWidgetId || showCreateForm) return
    const w = widgets.find(x => x.id === linkedWidgetId)
    if (w) openEdit(w)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedWidgetId, widgetsData])

  // This drawer isn't a route, so the router blocker can't see it close. All
  // three of the Drawer's exits (×, Escape, overlay) plus Cancel land here, so
  // one check covers them.
  const closeForm = async () => {
    if (formState.isDirty() && !(await confirm({ ...DISCARD_CHANGES_PROMPT }))) return
    formState.discardIfAutoCreated()
    setShowCreateForm(false)
    setEditingWidget(null)
    setLinkedWidget(null)
  }

  const handleSave = async () => {
    if (!formState.name.trim()) { toast('Widget name is required', 'error'); return }
    if (!formState.type) { toast('Widget type is required', 'error'); return }
    const metadataError = widgetMetadataSaveError(formState.type, formState.metadata)
    if (metadataError) { toast(metadataError, 'error'); return }
    setSaving(true)
    try {
      if (formState.widgetId) {
        await updateWidget(formState.widgetId, {
          name: formState.name.trim(),
          widget_type: formState.type,
          metadata: formState.metadata as Record<string, unknown>,
        })
        toast(formState.autoCreated ? 'Widget created' : 'Widget updated')
      } else {
        await createWidget({
          site_id: siteId,
          widget_type: formState.type,
          name: formState.name.trim(),
          metadata: formState.metadata as Record<string, unknown>,
        })
        toast('Widget created')
      }
      formState.markCommitted()
      await mutate()
      setShowCreateForm(false)
      setEditingWidget(null)
      setLinkedWidget(null)
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to save widget', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!formState.widgetId) return
    const ok = await confirm({
      title: 'Delete widget',
      confirmLabel: 'Delete',
      confirmVariant: 'danger',
      message: `Delete “${formState.name}”? This cannot be undone.`,
    })
    if (!ok) return
    try {
      await deleteWidget(formState.widgetId)
      formState.markCommitted() // prevent closeForm's discard path
      await mutate()
      setShowCreateForm(false)
      setEditingWidget(null)
      setLinkedWidget(null)
      toast('Widget deleted')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete widget', 'error')
    }
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.headerButtons}>
        <Button variant="secondary" size="small" onClick={() => setSite('')}>
          Switch Site
        </Button>
      </div>
      <div className={pageStyles.pageHeader}>
        <h1>Widget Library — {siteName}</h1>
      </div>

      {error && (
        <Banner variant="error" message={`Unable to load widgets: ${error.message || 'Connection failed'}`} />
      )}

      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          <div className={listTableStyles.searchInputGroup}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search widgets by name..."
              className={listTableStyles.searchInput}
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} className={listTableStyles.clearButton}>
                Clear
              </button>
            )}
          </div>
        </div>
        <div className={styles.filterRow}>
          <div className={styles.typeFilters}>
            <button
              type="button"
              className={`${styles.typeFilter} ${!typeFilter ? styles.typeFilterActive : ''}`}
              onClick={() => setTypeFilter('')}
            >
              All ({widgets.length})
            </button>
            {siteWidgetTypes.map(wt => {
              const isCar = isCarouselWidgetType(wt.code)
              const count = widgets.filter(w => isCar ? isCarouselWidgetType(w.widget_type) : w.widget_type === wt.code).length
              const active = typeFilter === wt.code || (isCar && isCarouselWidgetType(typeFilter))
              return (
                <button
                  key={wt.code}
                  type="button"
                  className={`${styles.typeFilter} ${widgetTypeColorClass(wt.code)} ${active ? styles.typeFilterActive : ''}`}
                  onClick={() => setTypeFilter(wt.code)}
                >
                  {wt.label} ({count})
                </button>
              )
            })}
          </div>
          <Button size="small" onClick={openCreate}>+ New Widget</Button>
        </div>
      </div>

      {filtered.length === 0 && !error && (
        <div className={styles.emptyState}>
          {widgets.length === 0
            ? <p>No widgets yet. Create one to get started.</p>
            : <p>No widgets match your filters.</p>
          }
        </div>
      )}

      {filtered.length > 0 && (
        <div className={styles.widgetGrid}>
          {filtered.map(w => {
            const sizeLabel = getWidgetSizeLabel(w.widget_type, w.metadata)
            return (
              <Panel key={w.id} margin="none" padding="none">
                <Link
                  to={`?widget=${w.id}`}
                  className={styles.widgetCard}
                  onClick={(e) => {
                    // Modified and middle clicks keep the browser's new-tab behaviour.
                    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
                    e.preventDefault()
                    openEdit(w)
                  }}
                >
                  <div className={styles.widgetCardHeader}>
                    <span className={styles.widgetName} title={w.name}>{w.name}</span>
                  </div>
                  <div className={styles.widgetCardPreview}>
                    {/* Artwork hides the placeholder's type label, so the badge rides on it. */}
                    {(w.widget_type === 'video_embed' && w.metadata?.video_id) || (w.widget_type === 'banner_image' && w.preview_url) ? (
                      <WidgetTypeBadge code={w.widget_type} className={styles.previewTypeBadge} />
                    ) : null}
                    {w.widget_type === 'video_embed' && w.metadata?.video_id ? (
                      <img
                        src={`https://cdn.jwplayer.com/v2/media/${w.metadata.video_id}/poster.jpg?width=480`}
                        alt={w.name}
                        className={styles.previewImage}
                      />
                    ) : w.widget_type === 'banner_image' && w.preview_url ? (
                      <img
                        src={w.preview_url}
                        alt={w.name}
                        className={styles.previewImage}
                      />
                    ) : (
                      <div className={`${styles.previewPlaceholder} ${widgetTypeColorClass(w.widget_type)}`}>
                        <WidgetTypeIcon code={w.widget_type} size={40} className={styles.previewPlaceholderIcon} />
                        <span>{getWidgetTypeLabel(w.widget_type)}</span>
                        {sizeLabel && <span className={styles.previewSize}>{sizeLabel}</span>}
                      </div>
                    )}
                  </div>
                </Link>
              </Panel>
            )
          })}
        </div>
      )}

      {/* Create / Edit Drawer */}
      <Drawer
        isOpen={showCreateForm}
        onClose={closeForm}
        title={editingWidget ? `Edit: ${editingWidget.name}` : 'New Widget'}
        headerActions={editingWidget ? <WidgetTypeBadge code={editingWidget.widget_type} /> : undefined}
        footer={formState.type ? (
          <>
            {editingWidget && (
              <Button
                variant="danger"
                size="small"
                onClick={handleDelete}
                disabled={widgetPlacements.length > 0 || placementsLoading}
                title={widgetPlacements.length > 0 ? 'Remove from all placements before deleting' : undefined}
              >
                Delete
              </Button>
            )}
            <div className={styles.footerRight}>
              <Button variant="secondary" onClick={closeForm}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving || formState.autoCreating}>
                {saving ? 'Saving...' : editingWidget ? 'Save Changes' : 'Create Widget'}
              </Button>
            </div>
          </>
        ) : undefined}
      >
        {/* Keyed so editing a different widget remounts the form — metadata editors
            hold uncontrolled state (MarkdownEditor seeds once on mount) and would
            otherwise show the previously edited widget's text after reset(). */}
        <WidgetForm key={editingWidget?.id ?? 'new'} state={formState} />

        {editingWidget && (
          <div className={`${formStyles.fieldGroup} ${styles.placementsSection}`}>
            <label>Placements ({placementsLoading ? '...' : widgetPlacements.length})</label>
            {widgetPlacements.length === 0 && !placementsLoading && (
              <div className={styles.widgetMetaEmpty}>Not used by any placements</div>
            )}
            {widgetPlacements.length > 0 && (
              <ul className={styles.placementList}>
                {widgetPlacements.map(p => (
                  <li key={p.id}>
                    <Link to={placementPath(p)} className={styles.placementLink}>
                      <span className={styles.placementListPage}>
                        {p.page ? p.page.name : getPageTypeLabel(p.page_type)}
                      </span>
                      {p.slug && <span className={styles.placementListMeta}>{p.slug}</span>}
                      <span className={styles.placementListMeta}>{p.region.name}</span>
                      <Badge variant={p.is_currently_active ? 'success' : 'neutral'}>
                        {p.is_currently_active ? 'Active' : 'Upcoming'}
                      </Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default WidgetLibrary
